import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Stack,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import RefreshIcon from '@mui/icons-material/Refresh';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CodeIcon from '@mui/icons-material/Code';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import TimelineIcon from '@mui/icons-material/Timeline';

import FieldLineageChain from './FieldLineageChain';
// MappingHealthTab removed — Readiness rollout absorbed the surface.
import type { TransformationTestResponse, MappingResponse, FieldMapping, TransformationStep, MappingHealthResponse } from '../services/api';
import type { CanvasNodeData, CanvasSelection, LineageChain } from '../canvas/types';
import { ActivityLogTab, LogicEditorTab, TransformationFormValues } from '../canvas/InspectorTabs';
import type { CanvasManualAdjustmentModel, CanvasPromptRunRecordModel } from '../services/api';
// Issue #656: field-scoped Inspector sections (Field Detail tab).
import FieldReviewItems from '../canvas/inspector/FieldReviewItems';
import FieldRules, {
  type ValidatorRule,
  type ValidatorOverrideEntry,
} from '../canvas/inspector/FieldRules';
import FieldSampleValues from '../canvas/inspector/FieldSampleValues';
import type { ReviewItem } from '../canvas/hooks/useReviewItems';

interface PreviewPanelProps {
  open: boolean;
  loading: boolean;
  error: string | null;
  data: TransformationTestResponse | null;
  notice: string | null;
  nodeLabel: string | null;
  nodeData?: CanvasNodeData;
  onClose: () => void;
  onRefresh?: () => void;
  onOpenDataPreview?: () => void;
  // Activity Log Props
  promptRuns?: CanvasPromptRunRecordModel[];
  edgeFieldMap?: Record<string, string>;
  // History Props
  manualAdjustments?: CanvasManualAdjustmentModel[];
  onUndoAdjustment?: (id: string) => Promise<void>;
  onRedoAdjustment?: (id: string) => Promise<void>;
  undoingAdjustmentId?: string | null;
  redoingAdjustmentId?: string | null;
  // Logic Editor Props
  selection?: CanvasSelection | null;
  mappingResponse?: MappingResponse;
  onSaveMapping?: (mapping: FieldMapping, values: TransformationFormValues, activeStep: TransformationStep | undefined) => void;
  isSavingMapping?: boolean;
  onCreateMapping?: (targetTable: string, targetField: string, sourceFile?: string, sourceField?: string, transformation?: string, transformationType?: string, operation?: string, parameters?: Record<string, any>) => void;
  onRegenerateMapping?: (mapping: FieldMapping, context: string) => void;
  isRegeneratingMapping?: boolean;
  onValidateMapping?: (expression: string) => void;
  isValidatingMapping?: boolean;
  onFeedbackSuccess?: () => void;
  onDeleteMapping?: (mapping: FieldMapping) => void;
  isDeletingMapping?: boolean;
  computedUpstream?: string[];
  computedDownstream?: string[];
  lineageChains?: LineageChain[];
  // Health Props
  healthData?: MappingHealthResponse | null;
  healthLoading?: boolean;
  healthError?: boolean;
  onApplyFix?: (issue: import('../services/api').HealthIssue, fixType: string) => void;
  // Issue #656: field-scoped Inspector data (Field Detail tab).
  batchId?: string;
  fieldReviewItems?: ReviewItem[];
  fieldValidatorRules?: ValidatorRule[];
  fieldValidatorOverrides?: ValidatorOverrideEntry[];
  fieldIsPrimaryKey?: boolean;
  overriddenRuleNames?: Set<string>;
  sampleOutput?: Record<string, Array<Record<string, unknown>>>;
  onMaterializeSamples?: () => void;
  materializing?: boolean;
  resolveDisabled?: boolean;
  /** Plan §G/9: unmapped review item primary action — jump to inline edit on the matching row. */
  onSelectInlineEdit?: (target_table: string, target_field: string) => void;
}

const PANEL_WIDTH = 380;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`preview-tabpanel-${index}`}
      aria-labelledby={`preview-tab-${index}`}
      style={{ flex: 1, overflow: 'hidden', display: value === index ? 'flex' : 'none', flexDirection: 'column' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PreviewPanel: React.FC<PreviewPanelProps> = (props) => {
  const {
    open,
    nodeLabel,
    nodeData,
    onClose,
    onRefresh,
    onOpenDataPreview,
    data,
  } = props;
  const [minimized, setMinimized] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  // ── Filter health data to the selected node's scope ──
  const filteredHealthData = useMemo((): typeof props.healthData => {
    if (!props.healthData) return props.healthData;

    const targetTable = nodeData?.table || nodeData?.mapping?.target_table || '';
    const targetField = nodeData?.field || nodeData?.mapping?.target_field || '';
    const kind = nodeData?.kind;

    // No specific node selected → show all issues
    if (!targetTable && !targetField) return props.healthData;

    const filtered = props.healthData.issues.filter((issue) => {
      // For field-level nodes, filter to matching table + field
      if (kind === 'targetField' || kind === 'sourceField') {
        const tableMatch = !issue.target_table || !targetTable ||
          issue.target_table.toLowerCase() === targetTable.toLowerCase();
        const fieldMatch = !targetField || !issue.target_field ||
          issue.target_field.toLowerCase() === targetField.toLowerCase();
        return tableMatch && fieldMatch;
      }
      // For table group nodes, filter to matching table
      if (kind === 'tableGroup' || kind === 'tableNode') {
        return !issue.target_table || !targetTable ||
          issue.target_table.toLowerCase() === targetTable.toLowerCase();
      }
      // For other nodes (transformation, etc.) show all
      return true;
    });

    const hasErrors = filtered.some((i) => i.severity === 'error');
    const hasWarnings = filtered.some((i) => i.severity === 'warning');

    return {
      ...props.healthData,
      issues: filtered,
      overall_status: hasErrors ? 'errors' : hasWarnings ? 'warnings' : 'healthy',
      summary: {
        ...props.healthData.summary,
        schema_rule_violations: filtered.filter((i) => i.category === 'schema_rule').length,
        expression_errors: filtered.filter((i) => i.category === 'expression_failure').length,
        unmapped_targets: filtered.filter((i) => i.category === 'coverage').length,
      },
    };
  }, [props.healthData, nodeData?.table, nodeData?.mapping?.target_table, nodeData?.mapping?.target_field, nodeData?.field, nodeData?.kind]);

  // Reset tab on new node selection — auto-switch to Health tab if selected field has errors
  useEffect(() => {
    if (open) {
      const hasErrors = filteredHealthData && filteredHealthData.issues.some(i => i.severity === 'error');
      setTabIndex(hasErrors ? 3 : 0);
    }
  }, [nodeLabel, open, filteredHealthData]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  if (!open) return null;

  const showLogicTab = !!nodeData?.step || !!nodeData?.mapping || nodeData?.kind === 'targetField' || !!nodeData?.isUnmapped;
  // Issue #656: Field Detail tab — when a field is selected, shows
  // Review items + Rules + Samples. When a table group is selected
  // (no target_field), shows table-scoped review items only — plan §G/10.
  const targetTable = nodeData?.table || nodeData?.mapping?.target_table;
  const targetField = nodeData?.field || nodeData?.mapping?.target_field;
  const isTableScope =
    Boolean(targetTable) &&
    !targetField &&
    (nodeData?.kind === 'tableGroup' || nodeData?.kind === 'tableNode');
  const showFieldDetailTab = Boolean(targetTable && targetField) || isTableScope;

  return (
    <Box
      sx={{
        width: minimized ? 48 : PANEL_WIDTH,
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        flexShrink: 0,
        height: '100%',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 48,
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: minimized ? 'center' : 'space-between',
          borderBottom: minimized ? 'none' : '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: 'background.default',
          px: minimized ? 0 : 1,
        }}
      >
        {minimized ? (
          <Tooltip title="Expand Inspector" placement="left">
            <IconButton size="small" onClick={() => setMinimized(false)}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: 'hidden', flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                Inspector
              </Typography>
              {nodeLabel && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto' }} />
                  <Typography variant="body2" color="primary.main" fontWeight={500} noWrap>
                    {nodeLabel}
                  </Typography>
                </>
              )}
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {onOpenDataPreview && (
                <Tooltip title="Open Data Preview">
                  <IconButton size="small" onClick={onOpenDataPreview}>
                    <DataObjectIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onRefresh && (
                <Tooltip title="Refresh Data">
                  <IconButton size="small" onClick={onRefresh}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Collapse">
                <IconButton size="small" onClick={() => setMinimized(true)}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close Panel">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </>
        )}
      </Box>

      {/* Tabs + Content (only when not minimized) */}
      {!minimized && (
        <>
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40 }}
            >
              <Tab
                icon={<CodeIcon sx={{ fontSize: 14 }} />}
                iconPosition="start"
                label="Logic"
                disabled={!showLogicTab}
                sx={{ minHeight: 40, textTransform: 'none', fontSize: 12, px: 1.5, minWidth: 0 }}
              />
              <Tab
                icon={<AccountTreeIcon sx={{ fontSize: 14 }} />}
                iconPosition="start"
                label="Lineage"
                sx={{ minHeight: 40, textTransform: 'none', fontSize: 12, px: 1.5, minWidth: 0 }}
              />
              <Tab
                icon={<TimelineIcon sx={{ fontSize: 14 }} />}
                iconPosition="start"
                label="Activity"
                sx={{ minHeight: 40, textTransform: 'none', fontSize: 12, px: 1.5, minWidth: 0 }}
              />
              {/* Health tab removed in Readiness rollout — findings live
                  on the Readiness chip in the canvas toolbar and on
                  consumer-node inspectors. Tab indices below stay 0/1/2/4
                  to avoid a sweep through every host re-numbering the
                  CustomTabPanel ``index`` props; the gap at index 3 is
                  intentional and tracked. */}
              {/* Issue #656: Field Detail — review items + rules + samples for the selected field. */}
              {showFieldDetailTab && (
                <Tab
                  icon={<MonitorHeartIcon sx={{ fontSize: 14 }} />}
                  iconPosition="start"
                  label={
                    props.fieldReviewItems && props.fieldReviewItems.length > 0
                      ? `Field (${props.fieldReviewItems.length})`
                      : 'Field'
                  }
                  sx={{ minHeight: 40, textTransform: 'none', fontSize: 12, px: 1.5, minWidth: 0 }}
                />
              )}
            </Tabs>
          </Box>

          {/* Tab 0: Logic */}
          <CustomTabPanel value={tabIndex} index={0}>
            <LogicEditorTab
              selection={props.selection ?? null}
              mappingResponse={props.mappingResponse}
              onSave={props.onSaveMapping}
              saving={props.isSavingMapping}
              onCreate={props.onCreateMapping}
              onRegenerate={props.onRegenerateMapping}
              regenerating={props.isRegeneratingMapping}
              onValidate={props.onValidateMapping}
              validating={props.isValidatingMapping}
              onDelete={props.onDeleteMapping}
              deleting={props.isDeletingMapping}
              healthIssues={filteredHealthData?.issues}
            />
          </CustomTabPanel>

          {/* Tab 1: Lineage (vertical field-level chain) */}
          <CustomTabPanel value={tabIndex} index={1}>
            <FieldLineageChain
              currentNode={nodeLabel || 'Selected Node'}
              currentNodeKind={nodeData?.kind}
              chains={props.lineageChains ?? []}
              upstream={
                props.computedUpstream && props.computedUpstream.length > 0
                  ? props.computedUpstream
                  : (nodeData?.mapping?.source_file
                      ? [`${nodeData.mapping.source_file} · ${nodeData.mapping.source_field}`]
                      : (data?.dependent_sources || []))
              }
              downstream={props.computedDownstream || []}
            />
          </CustomTabPanel>

          {/* Tab 2: Activity */}
          <CustomTabPanel value={tabIndex} index={2}>
            <ActivityLogTab
              manualAdjustments={props.manualAdjustments}
              promptRuns={props.promptRuns}
              selectedTargetField={props.nodeLabel}
              edgeFieldMap={props.edgeFieldMap}
              onUndo={props.onUndoAdjustment}
              onRedo={props.onRedoAdjustment}
              undoingId={props.undoingAdjustmentId}
              redoingId={props.redoingAdjustmentId}
            />
          </CustomTabPanel>

          {/* Tab 3 (Health) intentionally absent — see Tab-row comment
              above. Findings are now surfaced via the Readiness chip /
              ReadinessPanel in consumer nodes and the canvas toolbar. */}

          {/* Tab 4: Field Detail — Issue #656.
              - Field scope: Review items + Rules + Samples.
              - Table scope: Review items scoped to the table only. */}
          {showFieldDetailTab && (
            <CustomTabPanel value={tabIndex} index={4}>
              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    {isTableScope
                      ? `Review items for ${targetTable}`
                      : 'Review items for this field'}
                  </Typography>
                  {props.batchId ? (
                    <FieldReviewItems
                      batchId={props.batchId}
                      items={props.fieldReviewItems ?? []}
                      resolveDisabled={props.resolveDisabled}
                      overriddenRuleNames={props.overriddenRuleNames}
                      onSelectInlineEdit={props.onSelectInlineEdit}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Inspector available once the recipe is loaded.
                    </Typography>
                  )}
                </Box>
                {!isTableScope && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Rules on this field
                    </Typography>
                    <FieldRules
                      target_table={targetTable as string}
                      target_field={targetField as string}
                      rules={props.fieldValidatorRules}
                      overrides={props.fieldValidatorOverrides}
                      isPrimaryKey={props.fieldIsPrimaryKey}
                    />
                  </Box>
                )}
                {!isTableScope && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Sample values
                    </Typography>
                    <FieldSampleValues
                      target_table={targetTable as string}
                      target_field={targetField as string}
                      mapping={(nodeData?.mapping ?? null) as FieldMapping | null}
                      sampleOutput={props.sampleOutput}
                      onMaterialize={props.onMaterializeSamples}
                      materializing={props.materializing}
                    />
                  </Box>
                )}
                {isTableScope && (
                  <Typography variant="caption" color="text.secondary">
                    Click a target field in the canvas or table to see its
                    rules and sample values.
                  </Typography>
                )}
              </Box>
            </CustomTabPanel>
          )}
        </>
      )}
    </Box>
  );
};

export default React.memo(PreviewPanel);
